import React, { useCallback, useState } from 'react';
import { navigateToUrl } from '@splunk/dashboard-utils';
import Button from '@splunk/react-ui/Button';
import P from '@splunk/react-ui/Paragraph';
import type { SwitchClickHandler } from '@splunk/react-ui/Switch';
import { Modal } from '../Modal';
import {
    type ProtectedUrlModalProps,
    StyledNote,
    StyledSwitch,
    StyledUrl,
    TEXT,
    redirectionStyle,
} from './constants';

type ValidUrlModalProps = Omit<ProtectedUrlModalProps, 'event'> & {
    url?: string;
    newTab?: boolean;
};

export const ValidUrlModal = ({
    url,
    newTab,
    onRequestClose,
    onAddUrlToAllowList,
    ...rest
}: ValidUrlModalProps) => {
    const [isDontAskChecked, setIsDontAskChecked] = useState(false);

    const handleClose = useCallback(() => {
        onRequestClose?.();
    }, [onRequestClose]);

    const handleAllow = useCallback(() => {
        if (url) {
            if (isDontAskChecked) {
                onAddUrlToAllowList?.(url);
            }
            navigateToUrl({ url, newTab });
        }

        handleClose();
    }, [url, newTab, isDontAskChecked, handleClose, onAddUrlToAllowList]);

    const handleDontAsk = useCallback<SwitchClickHandler>(
        (_e, { selected }) => {
            setIsDontAskChecked(!selected);
        },
        []
    );

    return (
        <Modal
            onRequestClose={handleClose}
            open={!!url}
            divider="none"
            {...rest}
        >
            <Modal.Header title={TEXT.TITLE} />
            <Modal.Body>
                <StyledUrl>
                    <P style={redirectionStyle}>{TEXT.REDIRECTION}</P>
                    <P>{url}</P>
                    <StyledNote>{TEXT.NOTE}</StyledNote>
                </StyledUrl>
                {typeof onAddUrlToAllowList === 'function' && (
                    <StyledSwitch
                        selected={isDontAskChecked}
                        onClick={handleDontAsk}
                        appearance="checkbox"
                        inline
                    >
                        <P>{TEXT.ALLOW_AND_DONT_ASK}</P>
                        <StyledNote>{TEXT.ALLOW_AND_DONT_ASK_NOTE}</StyledNote>
                    </StyledSwitch>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    appearance="secondary"
                    onClick={handleClose}
                    label={TEXT.CANCEL}
                />
                <Button
                    appearance="primary"
                    onClick={handleAllow}
                    label={TEXT.CONTINUE}
                />
            </Modal.Footer>
        </Modal>
    );
};
